import { defaultsDeep, toPlainObject } from 'lodash'

import { empty } from '@sigma-frontend-libs/commons'
import { isUUID, splitRegexpGroupVariationsIntoArray } from '@sigmasms/commons'
import { EConditionsTags } from '@sigmasms/conditions'
import { SendingTypes } from '@sigmasms/shared-types'

import { isEmpty, resolveConditionTag, validateConditionValue } from '@/utils'

export const setTariffPrice = (price, source) => {
  if (Array.isArray(price)) {
    price = [ ...price ]
  } else {
    price = [ price ]
  }

  price.push(0)

  switch (source) {
    case SendingTypes.voice:
      price.length = 2
      break
    default:
      price.splice(1, price.length)
      break
  }

  return price
}

const parseTariffPriceValue = price => {
  if (price === undefined || price === null) {
    return undefined
  }

  if (typeof price === 'number') {
    if (Number.isFinite(price) && price >= 0) {
      return price
    }

    return undefined
  }

  if (typeof price === 'string') {
    const normalizedPrice = price.trim().replace(',', '.')

    if (normalizedPrice === '') {
      return undefined
    }

    const parsedPrice = Number.parseFloat(normalizedPrice)

    if (Number.isFinite(parsedPrice) && parsedPrice >= 0) {
      return parsedPrice
    }
  }

  return undefined
}

const getRequiredTariffPricesCount = source => {
  switch (source) {
    case SendingTypes.voice: {
      return 2
    }
    default: {
      return 1
    }
  }
}

const tariffPriceValueIsFilled = price => {
  return parseTariffPriceValue(price) !== undefined
}

export const tariffPriceIsValid = (price, source) => {
  const requiredTariffPricesCount = getRequiredTariffPricesCount(source)

  if (Array.isArray(price)) {
    if (price.length < requiredTariffPricesCount) {
      return false
    }

    for (let index = 0; index < requiredTariffPricesCount; index += 1) {
      if (!tariffPriceValueIsFilled(price[index])) {
        return false
      }
    }

    return true
  }

  if (requiredTariffPricesCount > 1) {
    return false
  }

  return tariffPriceValueIsFilled(price)
}

export const tariffRulesHaveValidPrices = (rules, source) => {
  if (!Array.isArray(rules)) {
    return true
  }

  for (const rule of rules) {
    if (!tariffPriceIsValid(rule.price, source)) {
      return false
    }
  }

  return true
}

const parseNumericValue = value => {
  if (isEmpty(value)) {
    return undefined
  }

  const parsed = Number.parseFloat(String(value).replace(',', '.'))
  if (Number.isFinite(parsed)) {
    return parsed
  }

  return undefined
}

const normalizeMessagePatternValue = value => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter(item => {
    return typeof item === 'string' && isUUID(item)
  })
}

const setTariffPriceRanges = value => {
  if (Array.isArray(value)) {
    if (!value.length) {
      return undefined
    }

    const result = {}
    value.forEach(({ key, price }) => {
      const parsedKey = parseNumericValue(key)
      const parsedPrice = parseNumericValue(price)
      if (!isEmpty(parsedKey) && !isEmpty(parsedPrice)) {
        result[parsedKey] = parsedPrice
      }
    })

    if (Object.keys(result).length) {
      return result
    }

    return undefined
  }

  if (!value) {
    return []
  }

  const values = []
  const keys = Object.keys(value).sort((a, b) => {
    return Number(a) - Number(b)
  })
  keys.forEach(key => {
    const price = parseNumericValue(value[key])
    if (!isEmpty(price)) {
      values.push({
        key,
        price
      })
    }
  })

  return values
}
const setTariffPackets = value => {
  if (!Array.isArray(value)) {
    return []
  }

  const result = value.reduce((accumulator, item, index) => {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      return accumulator
    }

    const parsedPrice = parseNumericValue(item.price)
    const parsedSize = parseNumericValue(item.size)
    let parsedPriority = parseNumericValue(item.priority)
    if (!Number.isInteger(parsedPriority) || parsedPriority < 0) {
      parsedPriority = index
    }

    if (
      !isEmpty(parsedPrice) &&
      parsedPrice > 0 &&
      Number.isInteger(parsedSize) &&
      parsedSize > 0
    ) {
      accumulator.push({
        price: parsedPrice,
        size: parsedSize,
        priority: parsedPriority
      })
    }

    return accumulator
  }, [])

  result.sort((firstPacket, secondPacket) => {
    return firstPacket.priority - secondPacket.priority
  })

  return result.map((packet, index) => {
    return {
      ...packet,
      priority: index
    }
  })
}
const getRuleGroupKey = ({ action, price, priceRanges, packets }) => {
  return JSON.stringify({
    action,
    price,
    priceRanges,
    packets
  })
}
const normalizeTariffCondition = (condition = {}) => {
  const { tag, path, value } = condition
  const resolvedConditionTag = resolveConditionTag(tag || path)
  if (!resolvedConditionTag) {
    return undefined
  }

  const normalizedValue = ruleProcessor(value, resolvedConditionTag)
  if (!validateConditionValue(resolvedConditionTag, normalizedValue)) {
    return undefined
  }

  return {
    tag: resolvedConditionTag,
    value: normalizedValue
  }
}
const normalizeTariffConditions = conditions => {
  if (!Array.isArray(conditions)) {
    return []
  }

  return conditions.reduce((accumulator, condition) => {
    const normalizedCondition = normalizeTariffCondition(condition)
    if (normalizedCondition) {
      accumulator.push(normalizedCondition)
    }

    return accumulator
  }, [])
}
const extractDefaultRule = (rules, source) => {
  let { price, priceRanges, packets, action, conditions } = rules[rules.length - 1] || {}
  conditions = normalizeTariffConditions(conditions)
  if (conditions.length === 0) {
    rules.pop()
    price = setTariffPrice(price, source)
    priceRanges = setTariffPriceRanges(priceRanges)
    packets = setTariffPackets(packets)

    return {
      price,
      priceRanges,
      packets,
      action
    }
  }
}

export const ruleProcessor = (rule, tagOrPath) => {
  let result

  const resolvedConditionTag = resolveConditionTag(tagOrPath)

  if (resolvedConditionTag) {
    switch (resolvedConditionTag) {
      case EConditionsTags.text:
      case EConditionsTags.recipient: {
        if (typeof rule === 'string') {
          result = rule
        } else {
          result = ''
        }
        break
      }
      case EConditionsTags.type:
      case EConditionsTags.group:
      case EConditionsTags.sender:
      case EConditionsTags.keywords:
      case EConditionsTags.contactList:
      case EConditionsTags.yulore: {
        if (Array.isArray(rule) && rule.length) {
          result = rule
          break
        }
        let rules = []
        if (typeof rule === 'string') {
          rules = splitRegexpGroupVariationsIntoArray(rule)
        }
        result = rules
        break
      }
      case EConditionsTags.messagePattern: {
        result = normalizeMessagePatternValue(rule)
        break
      }
      case EConditionsTags.mccmnc: {
        result = toPlainObject(rule)
        break
      }
    }
  }

  return result
}

const tariffRulesToGroup = (rules, source) => {
  const result = []
  if (Array.isArray(rules) && rules.length) {
    rules.sort((a, b) => {
      return a.priority - b.priority
    })

    let lastKey = ''
    for (let { action, price, priceRanges, packets, conditions } of rules) {
      price = setTariffPrice(price, source)
      priceRanges = setTariffPriceRanges(priceRanges)
      packets = setTariffPackets(packets)
      const normalizedConditions = normalizeTariffConditions(conditions)
      const rule = { conditions: normalizedConditions }
      const key = getRuleGroupKey({
        action,
        price,
        priceRanges,
        packets
      })
      if (key === lastKey) {
        if (normalizedConditions.length) {
          result[result.length - 1].rules.push(rule)
        }
      } else {
        let groupRules
        if (normalizedConditions.length) {
          groupRules = [ rule ]
        } else {
          groupRules = []
        }
        result.push({
          action,
          price,
          priceRanges,
          packets,
          rules: groupRules
        })
      }

      lastKey = key
    }
  }

  return result
}
const tariffGroupToRules = groups => {
  const result = []
  if (Array.isArray(groups) && groups.length) {
    for (const { action, price, priceRanges, packets, rules } of groups) {
      if (Array.isArray(rules) && rules.length) {
        for (const { conditions } of rules) {
          const normalizedConditions = normalizeTariffConditions(conditions)
          result.push({
            action,
            price,
            packets: setTariffPackets(packets),
            priceRanges: setTariffPriceRanges(priceRanges),
            priority: result.length,
            conditions: normalizedConditions
          })
        }
      } else {
        result.push({
          action,
          price,
          packets: setTariffPackets(packets),
          priceRanges: setTariffPriceRanges(priceRanges),
          priority: result.length,
          conditions: []
        })
      }
    }
  }

  return result
}

export const userTariffsInputFilter = tariff => {
  const { id, title, data, usersTariffs } = tariff || {}
  const result = {
    id,
    title,
    source: tariff.source,
    priority: usersTariffs?.priority || 0,
    $attach: true,
    _defaultRules: [],
    _defaultRule: {}
  }

  const [ , source ] = tariff.source?.split('.') || []
  if (data?.rules) {
    const _defaultRule = extractDefaultRule(data.rules, source)
    if (_defaultRule) {
      result._defaultRule = _defaultRule
    }

    result._defaultRules = tariffRulesToGroup(data.rules, source)
  }

  return result
}

export const userTariffsOutputFilter = tariff => {
  const { id, source, priority, $attach } = tariff || {}

  return {
    id,
    source,
    $attach: $attach === undefined || !!$attach,
    priority: priority || 0
  }
}

export const tariffInputFilter = tariff => {
  defaultsDeep(tariff, empty.tariffs.create)

  const rules = tariff.data.rules
  const [ , source ] = tariff.source?.split('.') || []

  if (Array.isArray(rules) && rules.length) {
    const _defaultRule = extractDefaultRule(tariff.data.rules, source)
    if (_defaultRule) {
      tariff._defaultRule = _defaultRule
    }

    tariff.data.rules = tariffRulesToGroup(rules, source)
  }

  return tariff
}

export const tariffOutputFilter = tariff => {
  tariff.data.rules = tariffGroupToRules(tariff.data.rules)

  if (tariff._defaultRule) {
    tariff.data.rules.push({
      action: tariff._defaultRule.action,
      price: tariff._defaultRule.price,
      packets: setTariffPackets(tariff._defaultRule.packets),
      priceRanges: setTariffPriceRanges(tariff._defaultRule.priceRanges),
      priority: tariff.data.rules.length,
      conditions: []
    })

    delete tariff._defaultRule
  }

  return tariff
}
